import type { ReplayRequestInput } from '@terra/shared-dto-chat-types';
import type { RequestMetadataRow } from '../electron/search-db';

function parseRequestHeaders({
  requestHeaders,
}: {
  requestHeaders: string | null;
}): Record<string, string> {
  if (!requestHeaders) {
    return {};
  }

  try {
    const parsed = JSON.parse(requestHeaders) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    const headers: Record<string, string> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'string') {
        headers[key] = value;
        continue;
      }
      if (Array.isArray(value)) {
        headers[key] = value.map(String).join(', ');
      }
    }
    return headers;
  } catch (error: unknown) {
    console.warn('[replayFromHistory] Failed to parse request headers', {
      error: error instanceof Error ? error : new Error(String(error)),
    });
    return {};
  }
}

function resolveReplayProtocol({
  httpVersion,
}: {
  httpVersion: string | null;
}): ReplayRequestInput['protocol'] {
  if (!httpVersion) {
    return undefined;
  }

  const normalized = httpVersion.toLowerCase();
  if (normalized.includes('2')) {
    return 'http2';
  }
  return 'http1';
}

export async function loadHistoryRowForReplay({
  targetId,
  targetType,
  requestId,
}: {
  targetId: string;
  targetType: string;
  requestId: string;
}): Promise<
  | { success: true; row: RequestMetadataRow; requestBody: string }
  | { success: false; error: string }
> {
  if (!window.electronAPI?.history?.getRows) {
    return { success: false, error: 'History API is not available' };
  }

  const result = await window.electronAPI.history.getRows({
    targetId,
    targetType,
    startRow: 0,
    endRow: 1,
    sortModel: [{ colId: 'timestamp', sort: 'desc' }],
    filters: {
      tqlWhereClause: 'r.id = ?',
      tqlParams: [requestId],
    },
  });

  if (!result.success || !result.data?.rows[0]) {
    return {
      success: false,
      error: `Request ${requestId} not found in proxy history`,
    };
  }

  const bodyResult = await window.electronAPI.bodyDb.get(requestId);
  if (!bodyResult.success) {
    console.warn('[replayFromHistory] Failed to load request body', {
      requestId,
      error: bodyResult.error,
    });
  }

  return {
    success: true,
    row: result.data.rows[0],
    requestBody: bodyResult.success ? (bodyResult.data?.requestBody ?? '') : '',
  };
}

export function buildReplayInputFromHistoryRow({
  row,
  requestBody,
  followRedirects,
  protocol,
}: {
  row: RequestMetadataRow;
  requestBody: string;
  followRedirects?: boolean;
  protocol?: ReplayRequestInput['protocol'];
}): ReplayRequestInput {
  return {
    action: 'send',
    method: row.method,
    url: row.url,
    headers: parseRequestHeaders({ requestHeaders: row.request_headers }),
    body: requestBody,
    protocol:
      protocol ?? resolveReplayProtocol({ httpVersion: row.http_version }),
    followRedirects,
  };
}
